import axios from "axios";
import {
    getAccessToken,
    clearTokens,
} from "../auth/tokenStorage";

export const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: 20000,
});

api.interceptors.request.use((config) => {
    const token = getAccessToken();

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

function getErrorMessage(error) {
    const detail = error.response?.data?.detail;

    if (typeof detail === "string") {
        return detail;
    }

    if (Array.isArray(detail)) {
        const messages = detail
            .map((item) => {
                if (typeof item === "string") {
                    return item;
                }

                return item?.msg ?? item?.message ?? null;
            })
            .filter(Boolean);

        if (messages.length > 0) {
            return messages.join(" ");
        }
    }

    if (detail && typeof detail === "object") {
        return JSON.stringify(detail);
    }

    if (!error.response) {
        return "Unable to connect to the server.";
    }

    return "Something went wrong. Please try again.";
}

function rejectApiError(error) {
    if (error instanceof Error) {
        // Keep the original Axios error, including response, status and config,
        // but replace its message with the useful backend message.
        error.message = getErrorMessage(error);

        return Promise.reject(error);
    }

    return Promise.reject(
        new Error("Something went wrong. Please try again.")
    );
}

let isRefreshing = false;
let queued = [];

function resolveQueue(newToken) {
    queued.forEach(({ resolve }) => {
        resolve(newToken);
    });

    queued = [];
}

function rejectQueue(error) {
    queued.forEach(({ reject }) => {
        reject(error);
    });

    queued = [];
}

api.interceptors.response.use(
    (response) => response,

    async (error) => {
        const status = error.response?.status;
        const original = error.config;

        // All non-401 errors reach this point.
        // This is where FastAPI's response.data.detail is extracted.
        if (status !== 401 || !original || original._retry) {
            return rejectApiError(error);
        }

        // A failed login is not an expired session.
        if (original.url?.includes("/auth/login")) {
            return rejectApiError(error);
        }

        // Do not attempt to refresh from the refresh request itself.
        if (original.url?.includes("/auth/refresh")) {
            clearTokens();
            return rejectApiError(error);
        }

        original._retry = true;

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                queued.push({
                    resolve: (newToken) => {
                        original.headers.Authorization =
                            `Bearer ${newToken}`;

                        resolve(api(original));
                    },
                    reject,
                });
            });
        }

        isRefreshing = true;

        try {
            const { refresh } = await import(
                "../auth/authService"
            );

            const newToken = await refresh();

            resolveQueue(newToken);

            original.headers.Authorization =
                `Bearer ${newToken}`;

            return api(original);
        } catch (refreshError) {
            rejectQueue(refreshError);
            clearTokens();
            window.location.assign("/login");

            return rejectApiError(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);